import { useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import {
  BotOff,
  ChevronRight,
  Clock,
  Database,
  Globe,
  Lock,
  PanelsTopLeft,
  Shapes,
  Star,
  Type,
  UserRound,
  UsersRound,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Application, UserWorkspace } from '@/api/types'
import { useSessionStore } from '@/features/auth/session-store'
import {
  useApplications,
  useFavoriteApplications,
  useSetFavorite,
} from '@/features/application/queries'
import {
  ACCESS_TAGS,
  MODIFIED_BUCKETS,
  UNKNOWN_OWNER,
  accessTags,
  byModified,
  byName,
  filterApplications,
  groupByOwner,
  isMine,
  membersById,
  ownerName,
  ownerOf,
  type ListingFilters,
  type ListingTab,
  type Owner,
  type OwnerGroup,
} from '@/features/application/listing'
import { memberLabel } from '@/features/workspace/members'
import { FilterChip, type FilterOption } from '@/shared/components/filter-chip'
import { SearchBox } from '@/shared/components/search-box'
import { GeneratedAvatar, UserAvatar } from '@/shared/components/user-avatar'
import { cn } from '@/shared/lib/utils'
import { byOrder } from '@/shared/lib/order'
import { dayHeading, whenText } from '@/shared/lib/when'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'

const ROUTE = '/shell/dashboard'

const split = (value: string | undefined): string[] =>
  value === undefined ? [] : value.split(',').filter((part) => part !== '')

const join = (values: readonly string[]): string | undefined =>
  values.length === 0 ? undefined : values.join(',')

const NO_FILTERS = { search: '', type: [], owner: [], access: [], modified: [] }

/**
 * The workspace's databases and dashboards as a table: the signed-in account's
 * own, everything by name, or everything folded under its owner.
 */
export function ApplicationList({ workspace }: { workspace: UserWorkspace }) {
  const { t } = useTranslation()
  const params = useSearch({ from: ROUTE })
  const navigate = useNavigate()
  const userId = useSessionStore((state) => state.user?.id)
  const { data, isPending } = useApplications(workspace.id)
  const favorites = useFavoriteApplications(workspace.id)

  const tab: ListingTab = params.tab ?? 'mine'
  const filters: ListingFilters = {
    search: params.search ?? '',
    type: split(params.type),
    owner: split(params.owner),
    access: split(params.access),
    modified: split(params.modified),
  }
  const filtered =
    filters.search !== '' ||
    [filters.type, filters.owner, filters.access, filters.modified].some(
      (values) => values.length > 0,
    )

  function go(changes: Partial<ListingFilters & { tab: ListingTab }>): void {
    const next = { tab, ...filters, ...changes }
    void navigate({
      to: '/dashboard',
      search: {
        tab: next.tab === 'mine' ? undefined : next.tab,
        search: next.search === '' ? undefined : next.search,
        type: join(next.type),
        owner: join(next.owner),
        access: join(next.access),
        modified: join(next.modified),
      },
      replace: true,
    })
  }

  if (isPending) return <Skeleton className="mt-6 h-32 w-full" />

  const applications = data ?? []
  if (applications.length === 0) {
    return (
      <Empty className="mt-6">
        <EmptyHeader>
          <EmptyTitle>{t('application.emptyTitle')}</EmptyTitle>
          <EmptyDescription>{t('application.emptyDescription')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  const members = membersById(workspace.users)
  const now = new Date()
  const inTab =
    tab === 'mine'
      ? applications.filter((application) => isMine(application, userId))
      : applications
  const shown = filterApplications(inTab, filters, members, now)

  const ownerOptions: FilterOption[] = groupByOwner(applications, members)
    .map((group) => ({
      value: group.key,
      label: ownerName(group) ?? t('application.ownerUnknown'),
      icon: <OwnerAvatar owner={group} className="size-5" />,
    }))
    .sort(
      (a, b) =>
        Number(a.value === UNKNOWN_OWNER) - Number(b.value === UNKNOWN_OWNER) ||
        a.label.localeCompare(b.label),
    )

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => {
        go({ tab: value as ListingTab })
      }}
      className="mt-6 gap-4"
    >
      <TabsList variant="line" aria-label={t('applicationList.views')}>
        <TabsTrigger value="mine">
          <UserRound />
          {t('applicationList.mine')}
        </TabsTrigger>
        <TabsTrigger value="name">
          <Type />
          {t('applicationList.byName')}
        </TabsTrigger>
        <TabsTrigger value="owner">
          <UsersRound />
          {t('applicationList.byOwner')}
        </TabsTrigger>
      </TabsList>

      <div className="flex flex-wrap items-center gap-2">
        <SearchBox
          term={filters.search}
          label={t('applicationList.search')}
          className="h-8 w-80 pr-7 pl-7 bg-background on-surface"
          onSearch={(term) => {
            go({ search: term })
          }}
        />
        <FilterChip
          label={t('applicationList.filters.type')}
          icon={<Shapes />}
          options={[
            {
              value: 'database',
              label: t('applicationList.type.database'),
              icon: <Database />,
            },
            {
              value: 'dashboard',
              label: t('applicationList.type.dashboard'),
              icon: <PanelsTopLeft />,
            },
          ]}
          selected={filters.type}
          onChange={(type) => {
            go({ type })
          }}
        />
        {tab !== 'mine' && (
          <FilterChip
            label={t('applicationList.filters.owner')}
            icon={<UserRound />}
            options={ownerOptions}
            selected={filters.owner}
            onChange={(owner) => {
              go({ owner })
            }}
          />
        )}
        <FilterChip
          label={t('applicationList.filters.access')}
          icon={<Lock />}
          options={ACCESS_TAGS.map((tag) => ({
            value: tag,
            label: t(`applicationList.access.${tag}`),
          }))}
          selected={filters.access}
          onChange={(access) => {
            go({ access })
          }}
        />
        <FilterChip
          label={t('applicationList.filters.modified')}
          icon={<Clock />}
          options={MODIFIED_BUCKETS.map((bucket) => ({
            value: bucket,
            label: t(`applicationList.modified.${bucket}`),
          }))}
          selected={filters.modified}
          onChange={(modified) => {
            go({ modified })
          }}
        />
        {filtered && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              go(NO_FILTERS)
            }}
          >
            {t('applicationList.filters.clear')}
          </Button>
        )}
      </div>

      <TabsContent value={tab}>
        {tab === 'mine' && inTab.length === 0 ? (
          <Empty className="mt-2">
            <EmptyHeader>
              <EmptyTitle>{t('applicationList.mineEmptyTitle')}</EmptyTitle>
              <EmptyDescription>
                {t('applicationList.mineEmptyDescription')}
              </EmptyDescription>
            </EmptyHeader>
            <Button
              variant="outline"
              className="on-surface"
              onClick={() => {
                go({ tab: 'name' })
              }}
            >
              {t('applicationList.browseAll')}
            </Button>
          </Empty>
        ) : shown.length === 0 ? (
          <Empty className="mt-2">
            <EmptyHeader>
              <EmptyTitle>{t('applicationList.noMatchTitle')}</EmptyTitle>
              <EmptyDescription>
                {t('applicationList.noMatchDescription')}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="rounded-lg border bg-background on-surface">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-4">
                    {t('applicationList.columns.name')}
                  </TableHead>
                  <TableHead>{t('applicationList.columns.details')}</TableHead>
                  <TableHead>{t('applicationList.columns.modified')}</TableHead>
                  <TableHead>{t('applicationList.columns.owner')}</TableHead>
                  <TableHead>{t('applicationList.columns.access')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tab === 'owner' ? (
                  <GroupedRows
                    groups={groupByOwner(shown, members)}
                    now={now}
                    workspaceId={workspace.id}
                    favorites={favorites}
                  />
                ) : (
                  [...shown]
                    .sort(tab === 'mine' ? byModified : byName)
                    .map((application) => (
                      <ApplicationRow
                        key={application.id}
                        application={application}
                        owner={ownerOf(application, members)}
                        now={now}
                        workspaceId={workspace.id}
                        favorite={favorites.has(application.id)}
                      />
                    ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </TabsContent>
    </Tabs>
  )
}

/** Each owner as a row that opens onto what they own. */
function GroupedRows({
  groups,
  now,
  workspaceId,
  favorites,
}: {
  groups: OwnerGroup[]
  now: Date
  workspaceId: number
  favorites: ReadonlySet<number>
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())

  return groups.flatMap((group) => {
    const expanded = open.has(group.key)
    return [
      <TableRow key={group.key}>
        <TableCell className="pl-4">
          <button
            type="button"
            aria-expanded={expanded}
            className="flex items-center gap-2 font-medium"
            onClick={() => {
              setOpen((current) => {
                const next = new Set(current)
                if (!next.delete(group.key)) next.add(group.key)
                return next
              })
            }}
          >
            <ChevronRight
              aria-hidden
              className={cn('size-4 transition-transform', expanded && 'rotate-90')}
            />
            <OwnerName owner={group} />
          </button>
        </TableCell>
        <TableCell className="text-muted-foreground">
          {t('applicationList.itemCount', { count: group.applications.length })}
        </TableCell>
        <TableCell>
          <Modified iso={group.updatedOn} now={now} />
        </TableCell>
        <TableCell colSpan={2} />
      </TableRow>,
      ...(expanded
        ? group.applications.map((application) => (
            <ApplicationRow
              key={application.id}
              application={application}
              owner={group}
              now={now}
              workspaceId={workspaceId}
              favorite={favorites.has(application.id)}
              nested
            />
          ))
        : []),
    ]
  })
}

function ApplicationRow({
  application,
  owner,
  now,
  workspaceId,
  favorite,
  nested = false,
}: {
  application: Application
  owner: Owner
  now: Date
  workspaceId: number
  favorite: boolean
  nested?: boolean
}) {
  const { t } = useTranslation()
  const closed = !application.user_can_access

  // In the unit the application holds, and nothing for one this account cannot
  // open: a closed database lists no tables, so it would report none.
  const holds = closed
    ? '—'
    : application.type === 'database'
      ? t('application.tableCount', { count: application.tables.length })
      : t('application.widgetCount', { count: application.widget_count })

  return (
    <TableRow
      className={cn('group/row', closed && 'text-muted-foreground')}
      title={
        closed ? t('applicationAccess.closed', { name: application.name }) : undefined
      }
    >
      <TableCell className={cn('max-w-96', nested ? 'pl-12' : 'pl-4')}>
        <span className="flex items-center gap-2">
          {application.type === 'dashboard' ? (
            <PanelsTopLeft aria-hidden className="size-4 shrink-0" />
          ) : (
            <Database aria-hidden className="size-4 shrink-0" />
          )}
          <span className="min-w-0 flex-1">
            <ApplicationName application={application} />
            {application.description !== undefined && application.description !== '' && (
              <span className="block truncate text-xs text-muted-foreground">
                {application.description}
              </span>
            )}
          </span>
          {/* Nothing to star on one this account cannot open. */}
          {!closed && (
            <FavoriteButton
              workspaceId={workspaceId}
              application={application}
              favorite={favorite}
            />
          )}
        </span>
      </TableCell>
      <TableCell className="text-muted-foreground">{holds}</TableCell>
      <TableCell>
        <Modified iso={application.updated_on} now={now} />
      </TableCell>
      <TableCell>
        <OwnerName owner={owner} />
      </TableCell>
      <TableCell>
        <span className="flex items-center gap-1">
          {accessTags(application).map((tag) => (
            <Badge key={tag} variant="outline" className="text-muted-foreground">
              {tag === 'everyone' ? <Globe /> : tag === 'noAi' ? <BotOff /> : <Lock />}
              {t(`applicationList.access.${tag}`)}
            </Badge>
          ))}
        </span>
      </TableCell>
    </TableRow>
  )
}

/** Stars or unstars it for this account; shown on hover until it is starred. */
function FavoriteButton({
  workspaceId,
  application,
  favorite,
}: {
  workspaceId: number
  application: Application
  favorite: boolean
}) {
  const { t } = useTranslation()
  const setFavorite = useSetFavorite()
  const label = favorite
    ? t('application.unfavoriteNamed', { name: application.name })
    : t('application.favoriteNamed', { name: application.name })

  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={label}
      aria-pressed={favorite}
      title={label}
      className={cn(
        'size-7 shrink-0 text-muted-foreground',
        !favorite && 'opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100',
      )}
      onClick={() => {
        setFavorite.mutate({
          workspaceId,
          applicationId: application.id,
          favorite: !favorite,
        })
      }}
    >
      <Star className={cn(favorite && 'fill-current text-foreground')} />
    </Button>
  )
}

/**
 * The name, as a link when there is something behind it: a closed application
 * opens onto nothing, and neither does a database with no tables.
 */
function ApplicationName({ application }: { application: Application }) {
  const className = 'block truncate font-medium'
  const name = <span className={className}>{application.name}</span>

  if (!application.user_can_access) return name

  if (application.type === 'database') {
    const first = byOrder(application.tables)[0]
    return first === undefined ? (
      name
    ) : (
      <Link
        className={cn(className, 'hover:underline')}
        to="/database/$databaseId/table/$tableId"
        params={{ databaseId: application.id, tableId: first.id }}
      >
        {application.name}
      </Link>
    )
  }

  return (
    <Link
      className={cn(className, 'hover:underline')}
      to="/dashboard/$dashboardId"
      params={{ dashboardId: application.id }}
    >
      {application.name}
    </Link>
  )
}

/** A person's picture, or a square marble tile that reads as software. */
function OwnerAvatar({ owner, className }: { owner: Owner; className: string }) {
  if (owner.kind === 'app') {
    return (
      <span className={cn('flex shrink-0 overflow-hidden rounded-md', className)}>
        <GeneratedAvatar seed={owner.key} variant="marble" />
      </span>
    )
  }
  return owner.kind === 'person' ? (
    <UserAvatar
      src={owner.member.avatar_url}
      seed={owner.member.email}
      className={className}
    />
  ) : (
    <UserAvatar src="" seed="" className={className} />
  )
}

function OwnerName({ owner }: { owner: Owner }) {
  const { t } = useTranslation()
  const name = ownerName(owner)
  return (
    <span className="flex min-w-0 items-center gap-2">
      <OwnerAvatar owner={owner} className="size-6" />
      <span className="min-w-0 text-left">
        <span className="flex items-center gap-1.5">
          <span className={cn('truncate', name === undefined && 'text-muted-foreground')}>
            {name ?? t('application.ownerUnknown')}
          </span>
          {owner.kind === 'app' && (
            <Badge variant="secondary" className="px-1.5">
              {t('applicationList.app')}
            </Badge>
          )}
        </span>
        {owner.kind === 'app' && (
          <span className="block truncate text-xs font-normal text-muted-foreground">
            {owner.manager === undefined
              ? t('applicationList.managedByNobody')
              : t('applicationList.managedBy', { name: memberLabel(owner.manager) })}
          </span>
        )}
      </span>
    </span>
  )
}

const THIS_YEAR = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })
const OTHER_YEAR = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })

/** Today, yesterday, or the date -- with its year only when that is not this one. */
function Modified({ iso, now }: { iso: string; now: Date }) {
  const { t } = useTranslation()
  const heading = dayHeading(iso, now)
  const at = new Date(iso)
  const text =
    heading.kind === 'today'
      ? t('applicationList.modified.today')
      : heading.kind === 'yesterday'
        ? t('applicationList.modified.yesterday')
        : (at.getFullYear() === now.getFullYear() ? THIS_YEAR : OTHER_YEAR).format(at)

  return (
    <time dateTime={iso} title={whenText(iso)} className="whitespace-nowrap">
      {text}
    </time>
  )
}
